// ============================================================================
// EMPLOYEE FORM MODAL
// ============================================================================
// Add / Edit form modal for the Employee master.
//
// Follows the prima-web mandatory form-modal blueprint (see CLAUDE.md):
//  - Internal formData state with refs for stable callbacks
//  - Initial snapshot for unsaved-changes (dirty) tracking
//  - Inline field validation via fieldErrors + getFieldError + clearFieldError
//  - 3-stage save flow: validate -> ConfirmDialog -> loading -> toast
//  - Discard-changes ConfirmDialog on close when isDirty
//  - Loading overlay during save (uses var(--modal-loading-overlay))
//  - Minimize-to-bar support (chevronDown header button) -- multi-instance Add
//
// modal-xl -- many fields organized into sections

import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog } from 'components/modals';
import { SyutSelect } from 'components/ui';
import { useModalMinimize } from 'contexts/ModalMinimizeContext';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface EmployeeWorkHourFormData {
  dayOfWeek: number;
  minimumMinutes: string;  // total minutes (internal — used for save)
  hours: string;           // display field — hours portion
  minutes: string;         // display field — minutes portion
  description: string;
}

export interface EmployeeFormData {
  code: string;
  name: string;
  userId: string;
  dateOfJoining: string;
  dateOfBirth: string;
  designationId: string;
  officeId: string;
  isPartner: boolean;
  isManager: boolean;
  managerId: string;
  partnerId: string;
  address: string;
  cityName: string;
  countryId: string;
  countryName: string;
  stateId: string;
  stateName: string;
  postalCode: string;
  phone1: string;
  phone2: string;
  email: string;
  standardRate: string;
  discountedRate: string;
  premiumRate: string;
  dateLeft: string;
  terminationReason: string;
  statusCode: 'ACTIVE' | 'INACTIVE';
  workHours: EmployeeWorkHourFormData[];
}

export interface EmployeeFormModalProps {
  isOpen: boolean;
  mode: 'create' | 'edit';
  initialData?: EmployeeFormData | null;
  isSaving?: boolean;
  apiError?: string | null;
  onSave: (data: EmployeeFormData) => void;
  onClose: () => void;
  // Lookup options passed from the screen
  userOptions: { value: string; label: string }[];
  designationOptions: { value: string; label: string }[];
  officeOptions: { value: string; label: string }[];
  managerOptions: { value: string; label: string }[];
  partnerOptions: { value: string; label: string }[];
  /** Map of managerId → partnerId for auto-filling partner when manager is selected */
  managerPartnerMap: Record<string, string>;
  countryOptions: { value: string; label: string }[];
  stateOptions: { value: string; label: string }[];
  timezoneOptions: { value: string; label: string }[];
  onCountryChange?: (countryId: string) => void;
  // Minimize-to-bar support
  tabId?: string;
  moduleName?: string;
  onRestoreModal?: (mode: 'create' | 'edit', instanceId?: string, recordCode?: string) => void;
  createInstanceId?: string;
  minimizeRef?: React.RefObject<(() => void) | null>;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const DAY_LABELS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const buildDefaultWorkHours = (): EmployeeWorkHourFormData[] =>
  Array.from({ length: 7 }, (_, i) => ({
    dayOfWeek: i + 1,
    minimumMinutes: '',
    hours: '',
    minutes: '',
    description: '',
  }));

/** Convert total minutes string to { hours, minutes } display strings */
const minutesToHrMin = (totalMin: string): { hours: string; minutes: string } => {
  const m = parseInt(totalMin, 10);
  if (isNaN(m) || m <= 0) return { hours: '', minutes: '' };
  return { hours: String(Math.floor(m / 60)), minutes: String(m % 60) };
};

/** Convert hours + minutes display strings to total minutes string */
const hrMinToMinutes = (hours: string, minutes: string): string => {
  const h = parseInt(hours, 10) || 0;
  const m = parseInt(minutes, 10) || 0;
  const total = h * 60 + m;
  return total > 0 ? String(total) : '';
};

const INITIAL_FORM_DATA: EmployeeFormData = {
  code: '',
  name: '',
  userId: '',
  dateOfJoining: '',
  dateOfBirth: '',
  designationId: '',
  officeId: '',
  isPartner: false,
  isManager: false,
  managerId: '',
  partnerId: '',
  address: '',
  cityName: '',
  countryId: '',
  countryName: '',
  stateId: '',
  stateName: '',
  postalCode: '',
  phone1: '',
  phone2: '',
  email: '',
  standardRate: '',
  discountedRate: '',
  premiumRate: '',
  dateLeft: '',
  terminationReason: '',
  statusCode: 'ACTIVE',
  workHours: buildDefaultWorkHours(),
};

const STATUS_OPTIONS = [
  { value: 'ACTIVE', label: 'Active' },
  { value: 'INACTIVE', label: 'Inactive' },
];

const CODE_PATTERN = /^[A-Z0-9_-]+$/;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const EmployeeFormModal: React.FC<EmployeeFormModalProps> = ({
  isOpen,
  mode,
  initialData,
  isSaving = false,
  apiError,
  onSave,
  onClose,
  userOptions,
  designationOptions,
  officeOptions,
  managerOptions,
  partnerOptions,
  managerPartnerMap,
  countryOptions,
  stateOptions,
  timezoneOptions,
  onCountryChange,
  tabId,
  moduleName,
  onRestoreModal,
  createInstanceId,
  minimizeRef,
}) => {
  const { minimize } = useModalMinimize();

  // --- Internal form state (Pattern B) ---
  const [formData, setFormData] = useState<EmployeeFormData>(INITIAL_FORM_DATA);
  const formDataRef = useRef(formData);
  formDataRef.current = formData;
  const modeRef = useRef(mode);
  modeRef.current = mode;
  const initialDataRef = useRef(initialData);
  initialDataRef.current = initialData;
  const createInstanceIdRef = useRef(createInstanceId);
  createInstanceIdRef.current = createInstanceId;
  const initialSnapshotRef = useRef('');
  const isRestoreRef = useRef(false);

  // --- Validation state ---
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const getFieldError = useCallback(
    (field: string) => fieldErrors[field],
    [fieldErrors]
  );
  const clearFieldError = useCallback((field: string) => {
    setFieldErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  }, []);

  // --- Confirm dialog state ---
  const [saveConfirmOpen, setSaveConfirmOpen] = useState(false);
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);

  // --- Populate / reset form on open. Skip on restore. ---
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!isOpen) return;
    if (isRestoreRef.current) {
      isRestoreRef.current = false;
      setFieldErrors({});
      return;
    }
    const src = initialDataRef.current;
    let data: EmployeeFormData;
    if (src) {
      // Convert minimumMinutes to hours+minutes display for existing data
      const wh = (src.workHours && src.workHours.length > 0 ? src.workHours : buildDefaultWorkHours()).map((w) => {
        const { hours, minutes } = minutesToHrMin(w.minimumMinutes);
        return { ...w, hours, minutes };
      });
      data = { ...src, workHours: wh };
    } else {
      data = { ...INITIAL_FORM_DATA, workHours: buildDefaultWorkHours() };
    }
    setFormData(data);
    initialSnapshotRef.current = JSON.stringify(data);
    setFieldErrors({});
  }, [isOpen]);

  // --- Dirty tracking ---
  const isDirty = useMemo(() => {
    if (!initialSnapshotRef.current) return false;
    return JSON.stringify(formData) !== initialSnapshotRef.current;
  }, [formData]);

  // --- Close handler ---
  const handleClose = useCallback(() => {
    if (isSaving) return;
    if (isDirty) {
      setDiscardConfirmOpen(true);
    } else {
      onClose();
    }
  }, [isDirty, onClose, isSaving]);

  // --- Minimize handler ---
  const handleMinimize = useCallback(() => {
    if (!tabId || !moduleName) return;
    const currentFormData = formDataRef.current;
    const currentMode = modeRef.current;
    const currentCreateInstanceId = createInstanceIdRef.current;

    let modalId: string;
    if (currentMode === 'create') {
      modalId = `${tabId}-form-modal-create-${currentCreateInstanceId || 'default'}`;
    } else {
      modalId = `${tabId}-form-modal-edit-${currentFormData.code || currentFormData.name || 'new'}`;
    }

    let title: string;
    if (currentMode === 'create') {
      const name = currentFormData.name?.trim();
      const code = currentFormData.code?.trim();
      const idx = currentCreateInstanceId || '1';
      title = name ? `Add - ${name} (${idx})` : code ? `Add - ${code} (${idx})` : `Add New (${idx})`;
    } else {
      title = `Edit - ${currentFormData.name || currentFormData.code || 'Employee'}`;
    }

    const savedFormData = { ...currentFormData, workHours: currentFormData.workHours.map((wh) => ({ ...wh })) };
    const savedSnapshot = initialSnapshotRef.current;
    const currentIsDirty = savedSnapshot
      ? JSON.stringify(currentFormData) !== savedSnapshot
      : false;

    minimize({
      id: modalId,
      title,
      moduleName,
      tabId,
      isDirty: currentIsDirty,
      onRestore: () => {
        isRestoreRef.current = true;
        setFormData(savedFormData);
        initialSnapshotRef.current = savedSnapshot;
        if (onRestoreModal) {
          onRestoreModal(
            currentMode,
            currentMode === 'create' ? currentCreateInstanceId : undefined,
            currentMode !== 'create' ? currentFormData.code : undefined,
          );
        }
      },
    });
    onClose();
  }, [tabId, moduleName, minimize, onClose, onRestoreModal]);

  if (minimizeRef) minimizeRef.current = handleMinimize;

  // --- Field setter helper ---
  const setField = useCallback(
    (field: keyof EmployeeFormData, value: string | boolean) => {
      clearFieldError(field);
      setFormData((prev) => ({ ...prev, [field]: value }));
    },
    [clearFieldError]
  );

  // --- Validate then show save confirmation ---
  const handleSave = useCallback(() => {
    const errors: Record<string, string> = {};
    const code = formData.code.trim();
    const name = formData.name.trim();

    // Code
    if (!code) {
      errors.code = 'Code is required';
    } else if (code.length > 30) {
      errors.code = 'Code must be 30 characters or less';
    } else if (!CODE_PATTERN.test(code)) {
      errors.code = 'Code: uppercase letters, numbers, hyphens, underscores only';
    }

    // Name
    if (!name) {
      errors.name = 'Employee name is required';
    }

    // Date of Joining
    if (!formData.dateOfJoining) {
      errors.dateOfJoining = 'Date of joining is required';
    }

    // Designation
    if (!formData.designationId) {
      errors.designationId = 'Designation is required';
    }

    // Office
    if (!formData.officeId) {
      errors.officeId = 'Location is required';
    }

    // Rule 1: Manager required for non-partners
    if (!formData.isPartner && !formData.managerId) {
      errors.managerId = 'Reporting manager is required';
    }

    // Email
    if (formData.email && !EMAIL_PATTERN.test(formData.email)) {
      errors.email = 'Invalid email format';
    }

    // Rate validations
    const std = formData.standardRate ? parseFloat(formData.standardRate) : null;
    const disc = formData.discountedRate ? parseFloat(formData.discountedRate) : null;
    const prem = formData.premiumRate ? parseFloat(formData.premiumRate) : null;

    if (disc !== null && std !== null && disc > std) {
      errors.discountedRate = 'Discounted rate must not exceed standard rate';
    }
    if (prem !== null && std !== null && prem < std) {
      errors.premiumRate = 'Premium rate must not be less than standard rate';
    }

    // Date left
    if (formData.dateLeft && formData.dateOfJoining && formData.dateLeft < formData.dateOfJoining) {
      errors.dateLeft = 'Date left must not be before date of joining';
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      requestAnimationFrame(() => {
        const firstField = Object.keys(errors)[0];
        const el = document.querySelector<HTMLElement>(
          `[data-field="${firstField}"]`
        );
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          el.querySelector<HTMLInputElement>('input, textarea, select')?.focus();
        }
      });
      return;
    }

    setFieldErrors({});
    setSaveConfirmOpen(true);
  }, [formData]);

  // --- Confirmed save -> delegate to parent ---
  const handleConfirmSave = useCallback(() => {
    setSaveConfirmOpen(false);
    onSave({
      ...formData,
      code: formData.code.trim().toUpperCase(),
      name: formData.name.trim(),
    });
  }, [formData, onSave]);

  // --- Hierarchy logic ---
  const handleIsPartnerChange = useCallback((checked: boolean) => {
    setFormData((prev) => ({
      ...prev,
      isPartner: checked,
      isManager: checked ? true : prev.isManager,
      partnerId: checked ? '' : prev.partnerId,
    }));
  }, []);

  const handleIsManagerChange = useCallback((checked: boolean) => {
    setFormData((prev) => {
      if (prev.isPartner) return prev; // Cannot uncheck isManager when isPartner is true
      return { ...prev, isManager: checked };
    });
  }, []);

  // --- Work hours update ---
  const updateWorkHour = useCallback(
    (index: number, field: 'hours' | 'minutes' | 'description', value: string) => {
      setFormData((prev) => {
        const wh = [...prev.workHours];
        const updated = { ...wh[index], [field]: value };
        // Recalculate total minutes from hours + minutes display fields
        if (field === 'hours' || field === 'minutes') {
          updated.minimumMinutes = hrMinToMinutes(
            field === 'hours' ? value : updated.hours,
            field === 'minutes' ? value : updated.minutes,
          );
        }
        wh[index] = updated;
        return { ...prev, workHours: wh };
      });
    },
    []
  );

  if (!isOpen) return null;

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-xl">
        {/* Loading overlay */}
        {isSaving && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              background: 'var(--modal-loading-overlay)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 10,
              borderRadius: 'inherit',
              gap: '0.75rem',
            }}
          >
            <div
              style={{
                width: '2rem',
                height: '2rem',
                border: '3px solid var(--border-color)',
                borderTopColor: '#2563eb',
                borderRadius: '50%',
                animation: 'spin 0.8s linear infinite',
              }}
            />
            <span
              style={{
                fontSize: '0.85rem',
                color: 'var(--text-secondary)',
                fontWeight: 500,
              }}
            >
              Saving...
            </span>
          </div>
        )}

        {/* Header */}
        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <h2 className="modal-title">
            {mode === 'create' ? 'Add New Employee' : 'Edit Employee'}
          </h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
            {tabId && (
              <button
                className="modal-header-btn"
                onClick={handleMinimize}
                title="Minimize to bar"
                disabled={isSaving}
              >
                <IconSystem.chevronDown size={16} />
              </button>
            )}
            <button
              className="modal-close"
              onClick={handleClose}
              disabled={isSaving}
              title="Close"
            >
              <IconSystem.x size={20} />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="modal-content">
          {apiError && (
            <div
              style={{
                marginBottom: '0.75rem',
                padding: '0.625rem 0.75rem',
                background: 'rgba(220, 38, 38, 0.08)',
                border: '1px solid #dc2626',
                borderRadius: '4px',
                color: '#dc2626',
                fontSize: '0.8125rem',
                lineHeight: 1.4,
              }}
            >
              {apiError}
            </div>
          )}

          {/* ============================================================ */}
          {/* Section 1: Basic Information                                  */}
          {/* ============================================================ */}

          {/* Row 1: Code + Status */}
          <div className="form-grid-2">
            <div className="form-group" data-field="code">
              <label className="form-label">
                Code <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                className={`form-input${getFieldError('code') ? ' form-field-error' : ''}`}
                value={formData.code}
                maxLength={30}
                placeholder="e.g. EMP001"
                disabled={isSaving || mode === 'edit'}
                onChange={(e) => {
                  clearFieldError('code');
                  setFormData((prev) => ({ ...prev, code: e.target.value.toUpperCase() }));
                }}
              />
              {getFieldError('code') && (
                <div className="form-error-message">{getFieldError('code')}</div>
              )}
            </div>

            <div className="form-group" data-field="statusCode">
              <SyutSelect
                label="Status"
                value={formData.statusCode}
                options={STATUS_OPTIONS}
                isClearable={false}
                size="sm"
                onChange={(val) => {
                  setFormData((prev) => ({
                    ...prev,
                    statusCode: (val as 'ACTIVE' | 'INACTIVE') || 'ACTIVE',
                  }));
                }}
              />
            </div>
          </div>

          {/* Row 2: Employee Name */}
          <div className="form-group" data-field="name">
            <label className="form-label">
              Employee Name <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              className={`form-input${getFieldError('name') ? ' form-field-error' : ''}`}
              value={formData.name}
              maxLength={200}
              placeholder="Full name"
              disabled={isSaving}
              onChange={(e) => setField('name', e.target.value)}
            />
            {getFieldError('name') && (
              <div className="form-error-message">{getFieldError('name')}</div>
            )}
          </div>

          {/* Row 3: System User + Date of Joining */}
          <div className="form-grid-2">
            <div className="form-group" data-field="userId">
              <SyutSelect
                label="System User"
                value={formData.userId || undefined}
                options={userOptions}
                isClearable
                size="sm"
                placeholder="Link to login user..."
                onChange={(val) => {
                  setFormData((prev) => ({ ...prev, userId: (val as string) || '' }));
                }}
              />
            </div>

            <div className="form-group" data-field="dateOfJoining">
              <label className="form-label">
                Date of Joining <span className="text-danger">*</span>
              </label>
              <input
                type="date"
                className={`form-input${getFieldError('dateOfJoining') ? ' form-field-error' : ''}`}
                value={formData.dateOfJoining}
                disabled={isSaving}
                onChange={(e) => setField('dateOfJoining', e.target.value)}
              />
              {getFieldError('dateOfJoining') && (
                <div className="form-error-message">{getFieldError('dateOfJoining')}</div>
              )}
            </div>
          </div>

          {/* Row 4: Designation + Location */}
          <div className="form-grid-2">
            <div className="form-group" data-field="designationId">
              <SyutSelect
                label={<>Designation <span className="text-danger">*</span></>}
                value={formData.designationId}
                options={designationOptions}
                isClearable={false}
                size="sm"
                placeholder="Select designation..."
                error={getFieldError('designationId')}
                onChange={(val) => {
                  clearFieldError('designationId');
                  setFormData((prev) => ({ ...prev, designationId: (val as string) || '' }));
                }}
              />
            </div>

            <div className="form-group" data-field="officeId">
              <SyutSelect
                label={<>Location <span className="text-danger">*</span></>}
                value={formData.officeId}
                options={officeOptions}
                isClearable={false}
                size="sm"
                placeholder="Select location..."
                error={getFieldError('officeId')}
                onChange={(val) => {
                  clearFieldError('officeId');
                  setFormData((prev) => ({ ...prev, officeId: (val as string) || '' }));
                }}
              />
            </div>
          </div>

          {/* ============================================================ */}
          {/* Section 2: Hierarchy                                          */}
          {/* ============================================================ */}
          <h4 style={{ fontSize: '0.875rem', fontWeight: 600, margin: '1rem 0 0.5rem', color: 'var(--text-primary)' }}>
            Hierarchy
          </h4>

          {/* Is Partner + Is Manager checkboxes */}
          <div className="form-group" style={{ display: 'flex', gap: '2rem', alignItems: 'center' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.8125rem', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={formData.isPartner}
                disabled={isSaving}
                onChange={(e) => handleIsPartnerChange(e.target.checked)}
              />
              Is Partner
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.8125rem', cursor: formData.isPartner ? 'not-allowed' : 'pointer', opacity: formData.isPartner ? 0.6 : 1 }}>
              <input
                type="checkbox"
                checked={formData.isManager}
                disabled={isSaving || formData.isPartner}
                onChange={(e) => handleIsManagerChange(e.target.checked)}
              />
              Is Manager
            </label>
          </div>

          {/* Manager + Partner dropdowns */}
          <div className="form-grid-2">
            <div className="form-group" data-field="managerId">
              <SyutSelect
                label={formData.isPartner ? 'Manager' : <>Manager <span className="text-danger">*</span></>}
                value={formData.managerId}
                options={managerOptions}
                isClearable
                error={getFieldError('managerId')}
                size="sm"
                placeholder="Select manager..."
                onChange={(val) => {
                  const newManagerId = (val as string) || '';
                  setFormData((prev) => {
                    const updates: Partial<EmployeeFormData> = { managerId: newManagerId };
                    // Auto-fill partner from the selected manager's partner (if not isPartner)
                    if (!prev.isPartner && newManagerId) {
                      const mgrPartnerId = managerPartnerMap[newManagerId] || '';
                      updates.partnerId = mgrPartnerId;
                    } else if (!prev.isPartner && !newManagerId) {
                      updates.partnerId = '';
                    }
                    return { ...prev, ...updates };
                  });
                }}
              />
            </div>

            <div className="form-group" data-field="partnerId">
              <SyutSelect
                label={formData.isPartner ? 'Partner (self or select another)' : 'Partner (default from manager)'}
                value={formData.partnerId || undefined}
                options={partnerOptions}
                isClearable
                size="sm"
                placeholder={formData.isPartner ? 'Leave empty = self on save' : 'Select partner...'}
                onChange={(val) => {
                  setFormData((prev) => ({ ...prev, partnerId: (val as string) || '' }));
                }}
              />
            </div>
          </div>

          {/* ============================================================ */}
          {/* Section 3: Contact & Address                                  */}
          {/* ============================================================ */}
          <h4 style={{ fontSize: '0.875rem', fontWeight: 600, margin: '1rem 0 0.5rem', color: 'var(--text-primary)' }}>
            Contact & Address
          </h4>

          {/* Address */}
          <div className="form-group" data-field="address">
            <label className="form-label">Address</label>
            <textarea
              className="form-input"
              value={formData.address}
              rows={2}
              placeholder="Street address"
              disabled={isSaving}
              onChange={(e) => setField('address', e.target.value)}
              style={{ resize: 'vertical' }}
            />
          </div>

          {/* Country + State */}
          <div className="form-grid-2">
            <div className="form-group" data-field="countryId">
              <SyutSelect
                label="Country"
                value={formData.countryId}
                options={countryOptions}
                isClearable
                size="sm"
                placeholder="Select country..."
                onChange={(val) => {
                  const newCountryId = (val as string) || '';
                  const opt = countryOptions.find((o) => o.value === newCountryId);
                  setFormData((p) => ({
                    ...p,
                    countryId: newCountryId,
                    countryName: opt?.label?.split(' - ')?.[1] || opt?.label || '',
                    stateId: '',
                    stateName: '',
                  }));
                  onCountryChange?.(newCountryId);
                }}
              />
            </div>
            <div className="form-group" data-field="stateId">
              <SyutSelect
                label="State / Province"
                value={formData.stateId}
                options={stateOptions}
                isClearable
                size="sm"
                placeholder={formData.countryId ? 'Select state...' : 'Select country first'}
                onChange={(val) => {
                  const newStateId = (val as string) || '';
                  const opt = stateOptions.find((o) => o.value === newStateId);
                  setFormData((p) => ({
                    ...p,
                    stateId: newStateId,
                    stateName: opt?.label?.split(' - ')?.[1] || opt?.label || '',
                  }));
                }}
              />
            </div>
          </div>

          {/* City + Postal Code */}
          <div className="form-grid-2">
            <div className="form-group" data-field="cityName">
              <label className="form-label">City</label>
              <input
                type="text"
                className="form-input"
                value={formData.cityName}
                maxLength={100}
                placeholder="e.g. Mumbai"
                disabled={isSaving}
                onChange={(e) => setField('cityName', e.target.value)}
              />
            </div>
            <div className="form-group" data-field="postalCode">
              <label className="form-label">Postal Code</label>
              <input
                type="text"
                className="form-input"
                value={formData.postalCode}
                maxLength={20}
                placeholder="e.g. 400001"
                disabled={isSaving}
                onChange={(e) => setField('postalCode', e.target.value)}
              />
            </div>
          </div>

          {/* Phone + Email */}
          <div className="form-grid-2">
            <div className="form-group" data-field="phone1">
              <label className="form-label">Phone</label>
              <input
                type="text"
                className="form-input"
                value={formData.phone1}
                maxLength={30}
                placeholder="e.g. +91-9876543210"
                disabled={isSaving}
                onChange={(e) => setField('phone1', e.target.value)}
              />
            </div>
            <div className="form-group" data-field="email">
              <label className="form-label">Email</label>
              <input
                type="text"
                className={`form-input${getFieldError('email') ? ' form-field-error' : ''}`}
                value={formData.email}
                maxLength={200}
                placeholder="e.g. john@firm.com"
                disabled={isSaving}
                onChange={(e) => setField('email', e.target.value)}
              />
              {getFieldError('email') && (
                <div className="form-error-message">{getFieldError('email')}</div>
              )}
            </div>
          </div>

          {/* ============================================================ */}
          {/* Section 4: Rates                                              */}
          {/* ============================================================ */}
          <h4 style={{ fontSize: '0.875rem', fontWeight: 600, margin: '1rem 0 0.5rem', color: 'var(--text-primary)' }}>
            Rates
          </h4>

          <div className="form-grid-3">
            <div className="form-group" data-field="standardRate">
              <label className="form-label">Standard Rate</label>
              <input
                type="number"
                className={`form-input${getFieldError('standardRate') ? ' form-field-error' : ''}`}
                value={formData.standardRate}
                step="0.01"
                min="0"
                placeholder="0.00"
                disabled={isSaving}
                onChange={(e) => setField('standardRate', e.target.value)}
              />
              {getFieldError('standardRate') && (
                <div className="form-error-message">{getFieldError('standardRate')}</div>
              )}
            </div>
            <div className="form-group" data-field="discountedRate">
              <label className="form-label">Discounted Rate</label>
              <input
                type="number"
                className={`form-input${getFieldError('discountedRate') ? ' form-field-error' : ''}`}
                value={formData.discountedRate}
                step="0.01"
                min="0"
                placeholder="0.00"
                disabled={isSaving}
                onChange={(e) => setField('discountedRate', e.target.value)}
              />
              {getFieldError('discountedRate') && (
                <div className="form-error-message">{getFieldError('discountedRate')}</div>
              )}
            </div>
            <div className="form-group" data-field="premiumRate">
              <label className="form-label">Premium Rate</label>
              <input
                type="number"
                className={`form-input${getFieldError('premiumRate') ? ' form-field-error' : ''}`}
                value={formData.premiumRate}
                step="0.01"
                min="0"
                placeholder="0.00"
                disabled={isSaving}
                onChange={(e) => setField('premiumRate', e.target.value)}
              />
              {getFieldError('premiumRate') && (
                <div className="form-error-message">{getFieldError('premiumRate')}</div>
              )}
            </div>
          </div>

          {/* ============================================================ */}
          {/* Section 5: Separation (edit mode only)                        */}
          {/* ============================================================ */}
          {mode === 'edit' && (
            <>
              <h4 style={{ fontSize: '0.875rem', fontWeight: 600, margin: '1rem 0 0.5rem', color: 'var(--text-primary)' }}>
                Separation
              </h4>

              <div className="form-grid-2">
                <div className="form-group" data-field="dateLeft">
                  <label className="form-label">Date Left</label>
                  <input
                    type="date"
                    className={`form-input${getFieldError('dateLeft') ? ' form-field-error' : ''}`}
                    value={formData.dateLeft}
                    disabled={isSaving}
                    onChange={(e) => setField('dateLeft', e.target.value)}
                  />
                  {getFieldError('dateLeft') && (
                    <div className="form-error-message">{getFieldError('dateLeft')}</div>
                  )}
                </div>
                <div className="form-group" data-field="terminationReason">
                  <label className="form-label">Termination Reason</label>
                  <input
                    type="text"
                    className="form-input"
                    value={formData.terminationReason}
                    maxLength={200}
                    placeholder="Reason for leaving"
                    disabled={isSaving}
                    onChange={(e) => setField('terminationReason', e.target.value)}
                  />
                </div>
              </div>
            </>
          )}

          {/* ============================================================ */}
          {/* Section 6: Weekly Minimum Hours                               */}
          {/* ============================================================ */}
          <h4 style={{ fontSize: '0.875rem', fontWeight: 600, margin: '1rem 0 0.5rem', color: 'var(--text-primary)' }}>
            Weekly Minimum Hours
          </h4>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
            {/* Header row */}
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-secondary)', padding: '0 0.25rem' }}>
              <div style={{ width: '90px' }}>Day</div>
              <div style={{ width: '70px', textAlign: 'center' }}>Hrs</div>
              <div style={{ width: '70px', textAlign: 'center' }}>Min</div>
              <div style={{ flex: 1 }}>Description</div>
            </div>
            {formData.workHours.map((wh, idx) => (
              <div key={wh.dayOfWeek} style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                <div style={{ width: '90px', fontSize: '0.8125rem', color: 'var(--text-primary)', fontWeight: 500 }}>
                  {DAY_LABELS[idx]}
                </div>
                <input
                  type="number"
                  className="form-input"
                  value={wh.hours}
                  min={0}
                  max={24}
                  placeholder="0"
                  disabled={isSaving}
                  style={{ width: '70px', fontSize: '0.8125rem', padding: '0.375rem 0.5rem', textAlign: 'center' }}
                  onChange={(e) => updateWorkHour(idx, 'hours', e.target.value)}
                />
                <input
                  type="number"
                  className="form-input"
                  value={wh.minutes}
                  min={0}
                  max={59}
                  placeholder="0"
                  disabled={isSaving}
                  style={{ width: '70px', fontSize: '0.8125rem', padding: '0.375rem 0.5rem', textAlign: 'center' }}
                  onChange={(e) => updateWorkHour(idx, 'minutes', e.target.value)}
                />
                <input
                  type="text"
                  className="form-input"
                  value={wh.description}
                  placeholder="Optional note"
                  disabled={isSaving}
                  style={{ flex: 1, fontSize: '0.8125rem', padding: '0.375rem 0.5rem' }}
                  onChange={(e) => updateWorkHour(idx, 'description', e.target.value)}
                />
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button
            className="btn btn-secondary"
            onClick={handleClose}
            disabled={isSaving}
          >
            Cancel
          </button>
          <button
            className="btn btn-primary"
            onClick={handleSave}
            disabled={isSaving}
          >
            {mode === 'create' ? 'Create' : 'Save Changes'}
          </button>
        </div>
      </div>

      {/* Save confirmation */}
      <ConfirmDialog
        open={saveConfirmOpen}
        title={mode === 'create' ? 'Confirm Create' : 'Confirm Update'}
        message={
          mode === 'create'
            ? `Create employee "${formData.name.trim()}"?`
            : `Save changes to "${formData.name.trim()}"?`
        }
        confirmLabel={mode === 'create' ? 'Create' : 'Save'}
        cancelLabel="Cancel"
        variant="default"
        onConfirm={handleConfirmSave}
        onCancel={() => setSaveConfirmOpen(false)}
      />

      {/* Discard changes confirmation */}
      <ConfirmDialog
        open={discardConfirmOpen}
        title="Discard Changes?"
        message="You have unsaved changes. Do you want to discard them?"
        confirmLabel="Discard"
        cancelLabel="Keep Editing"
        variant="danger"
        onConfirm={() => {
          setDiscardConfirmOpen(false);
          onClose();
        }}
        onCancel={() => setDiscardConfirmOpen(false)}
      />
    </>
  );
};

export default EmployeeFormModal;
